import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { Helmet } from "react-helmet";
import { FaSyncAlt, FaExchangeAlt, FaSignOutAlt, FaCalendarAlt } from "react-icons/fa";

const API_URL = `https://${process.env.REACT_APP_API_KEY_AUTH_BANKER_TRANS}.execute-api.ap-south-1.amazonaws.com/default/bankersTransaction`;
const BRANCH_LIST_URL = `https://${process.env.REACT_APP_API_KEY_AUTH_BANKER}.execute-api.ap-south-1.amazonaws.com/default/createBankers`;
const R_URI = process.env.REACT_APP_AUTH_DOMAIN;

export default function BankersTransaction() {
  const auth = useAuth();
  const userEmail = auth.user?.profile?.email || "Unknown";

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const [branchId, setBranchId] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("IN");
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);

  const resolveBranch = useCallback(async () => {
    try {
      const res = await fetch(BRANCH_LIST_URL);
      const data = await res.json();
      const match = (data.branches || []).find(b => b.email?.toLowerCase() === userEmail.toLowerCase());
      if (match) setBranchId(match.id);
    } catch (err) { console.error("[AUTH ERROR]", err); }
  }, [userEmail]);

  const fetchTransactions = useCallback(async (myId) => {
    if (!myId) return;
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      const all = data.transactions || [];
      const filtered = all.filter(tx => (tx.fromBranch === myId || tx.toBranch === myId) && tx.status !== "REJECTED");
      
      // Sorted in ASCENDING order (Oldest first)
      setTransactions(filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    } catch (err) { console.error("[FETCH ERROR]", err); }
    setLoading(false);
  }, []);

  useEffect(() => { resolveBranch(); }, [resolveBranch]);
  useEffect(() => { if (branchId) fetchTransactions(branchId); }, [branchId, fetchTransactions]);

  const handlePost = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.target);
    const payload = [{
      paymentId: `TX-${Date.now()}`,
      paymentType: selectedType,
      fromBranch: branchId,
      toBranch: branchId,
      amount: parseFloat(fd.get("amount")),
      commission: parseFloat(fd.get("commission")) || 0,
      name: fd.get("partyName"),
      remarks: fd.get("remarks"),
      timestamp: new Date().toISOString(),
      status: "DELIVERED"
    }];

    try {
      await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transactions: payload }),
      });
      e.target.reset();
      fetchTransactions(branchId);
    } catch (err) { window.alert("Post failed."); }
    setLoading(false);
  };

  const ledgerData = useMemo(() => {
    const rangeData = transactions.filter(tx => {
      const txDate = tx.timestamp.split('T')[0];
      return txDate >= startDate && txDate <= endDate;
    });

    const ins = rangeData.filter(tx => ["IN", "BTIN", "SPLITIN"].includes(tx.paymentType));
    const outs = rangeData.filter(tx => ["OUT", "BTOUT", "SPLITOUT"].includes(tx.paymentType));
    const exps = rangeData.filter(tx => tx.paymentType === "EXP");

    const totalIn = ins.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const totalOut = outs.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const totalExp = exps.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const totalComm = rangeData.reduce((sum, tx) => sum + Number(tx.commission || 0), 0);
    
    const opening = 0; 
    const closing = (opening + totalIn + totalComm) - (totalOut + totalExp);

    // Dynamic row length matches the longest list (Ins vs Outs)
    const rowCount = Math.max(ins.length, outs.length);
    const rows = [];
    for (let i = 0; i < rowCount; i++) {
      rows.push({ in: ins[i] || null, out: outs[i] || null });
    }

    return { rows, totalIn, totalOut, totalExp, totalComm, opening, closing, exps };
  }, [transactions, startDate, endDate]);

  return (
    <div style={{ padding: "10px", backgroundColor: "#fff", minHeight: "100vh", fontFamily: "sans-serif" }}>
      <Helmet><title>Banker Ledger</title></Helmet>
      
      <header style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
        <h2 style={{ fontSize: '18px' }}><FaExchangeAlt /> Banker Portal | {branchId}</h2>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={styles.dateRange}>
            <FaCalendarAlt />
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.dateInput} />
            <span>to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.dateInput} />
          </div>
          <button onClick={() => fetchTransactions(branchId)} style={styles.syncBtn}><FaSyncAlt /> Sync</button>
          <button onClick={() => { auth.removeUser(); window.location.href = R_URI; }} style={styles.signOutBtn}><FaSignOutAlt /></button>
        </div>
      </header>

      {/* QUICK ENTRY FORM */}
      <section style={styles.formContainer}>
        <form onSubmit={handlePost} style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} style={styles.input}>
            <option value="IN">IN</option><option value="OUT">OUT</option><option value="EXP">EXPENSE</option>
          </select>
          <input name="partyName" placeholder="Name" required style={{...styles.input, flex: 1}} />
          <input name="amount" type="number" placeholder="Amount" required style={styles.input} />
          <input name="commission" type="number" placeholder="Comm." style={styles.input} />
          <input name="remarks" placeholder="Remarks (Details)" required style={{...styles.input, flex: 1.5}} />
          <button type="submit" disabled={loading} style={styles.saveBtn}>Save Entry</button>
        </form>
      </section>

      <div style={{ display: "flex", gap: "10px" }}>
        <div style={{ flex: 5, overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr style={{ backgroundColor: "#f2f2f2" }}>
                <th style={styles.th}>S.NO</th>
                <th style={styles.th}>DATE</th>
                <th style={styles.th}>NAME (IN)</th>
                <th style={styles.th}>IN</th>
                <th style={styles.th}>COMM</th>
                <th style={styles.th}>DETAILS</th>
                <th style={styles.th}>NAME (OUT)</th>
                <th style={styles.th}>OUT</th>
                <th style={styles.th}>COMM</th>
                <th style={styles.th}>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {ledgerData.rows.length > 0 ? (
                ledgerData.rows.map((row, idx) => {
                  const displayDate = row.in?.timestamp.split('T')[0] || row.out?.timestamp.split('T')[0] || "";
                  return (
                    <tr key={idx} style={{ height: "28px" }}>
                      <td style={styles.tdCenter}>{idx + 1}</td>
                      <td style={styles.tdSmall}>{displayDate}</td>
                      <td style={styles.td}>{row.in?.name || ""}</td>
                      <td style={styles.tdBold}>{row.in ? row.in.amount.toLocaleString() : ""}</td>
                      <td style={{...styles.td, color: 'green'}}>{row.in?.commission || ""}</td>
                      <td style={styles.td}>{row.in?.remarks || ""}</td>
                      <td style={styles.td}>{row.out?.name || ""}</td>
                      <td style={styles.tdBold}>{row.out ? row.out.amount.toLocaleString() : ""}</td>
                      <td style={{...styles.td, color: 'green'}}>{row.out?.commission || ""}</td>
                      <td style={styles.td}>{row.out?.remarks || ""}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>No transactions found for this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ flex: 1, minWidth: "250px" }}>
          <table style={styles.summaryTable}>
            <tbody>
              <tr style={{ backgroundColor: "#92d050" }}>
                <td style={styles.summaryLabel}>OPENING</td>
                <td style={styles.summaryValueBlue}>{ledgerData.opening}</td>
              </tr>
              <tr style={{ height: "20px" }}><td colSpan="2"></td></tr>
              <tr style={{ backgroundColor: "#92d050" }}>
                <td style={styles.summaryLabel}>TOTAL IN</td>
                <td style={styles.summaryValue}>{ledgerData.totalIn.toLocaleString()}</td>
              </tr>
              <tr style={{ backgroundColor: "#92d050" }}>
                <td style={styles.summaryLabel}>TOTAL COMM (+)</td>
                <td style={{...styles.summaryValue, color: '#166534'}}>{ledgerData.totalComm.toLocaleString()}</td>
              </tr>
              <tr style={{ height: "10px" }}><td colSpan="2"></td></tr>
              <tr style={{ backgroundColor: "#92d050" }}>
                <td style={styles.summaryLabel}>TOTAL OUT</td>
                <td style={styles.summaryValue}>{ledgerData.totalOut.toLocaleString()}</td>
              </tr>
              <tr style={{ height: "20px" }}><td colSpan="2"></td></tr>
              <tr style={{ backgroundColor: "#92d050" }}>
                <td colSpan="2" style={{...styles.summaryLabel, textAlign: 'center'}}>EXPENSES DETAILS</td>
              </tr>
              {ledgerData.exps.map((exp, i) => (
                <tr key={i}>
                  <td style={styles.tdSmall}>{exp.name || "Exp"}</td>
                  <td style={styles.tdRightSmall}>{exp.amount.toLocaleString()}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: "#ffff00" }}>
                <td style={styles.summaryLabel}>EXP TOTAL</td>
                <td style={{ ...styles.summaryValue, color: "red" }}>{ledgerData.totalExp.toLocaleString()}</td>
              </tr>
              <tr style={{ backgroundColor: "#ffff00" }}>
                <td style={styles.summaryLabel}>CLOSING BAL.</td>
                <td style={styles.summaryValueBlue}>{ledgerData.closing.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      

    </div>
  );
}

const styles = {
  table: { width: "100%", borderCollapse: "collapse", border: "2px solid #000", fontSize: "11px" },
  th: { border: "1px solid #000", padding: "6px", textAlign: "left" },
  td: { border: "1px solid #ccc", padding: "4px 6px" },
  tdSmall: { border: "1px solid #ccc", padding: "4px", fontSize: "10px", color: "#444" },
  tdRightSmall: { border: "1px solid #ccc", padding: "4px", fontSize: "11px", textAlign: 'right' },
  tdCenter: { border: "1px solid #ccc", textAlign: "center" },
  tdBold: { border: "1px solid #ccc", fontWeight: "bold", padding: "4px 6px" },
  summaryTable: { width: "100%", borderCollapse: "collapse", border: "2px solid #000" },
  summaryLabel: { padding: "8px", fontWeight: "bold", border: "1px solid #000", fontSize: "12px" },
  summaryValue: { padding: "8px", textAlign: "right", border: "1px solid #000", fontWeight: "bold", fontSize: "13px" },
  summaryValueBlue: { padding: "8px", textAlign: "right", border: "1px solid #000", color: "#0070c0", fontWeight: "bold", fontSize: "13px" },
  formContainer: { marginBottom: "10px", padding: "10px", backgroundColor: "#f9f9f9", border: "1px solid #ddd", borderRadius: "5px" },
  input: { padding: "6px", border: "1px solid #ccc", borderRadius: "4px", fontSize: '12px' },
  saveBtn: { padding: "6px 15px", backgroundColor: "#28a745", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: 'bold' },
  dateRange: { display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", background: "#eee", padding: "4px 8px", borderRadius: "4px" },
  dateInput: { border: "1px solid #ccc", padding: "2px", fontSize: "11px" },
  syncBtn: { padding: "5px 12px", cursor: "pointer", borderRadius: "4px", border: "1px solid #ccc", backgroundColor: "#fff" },
  signOutBtn: { padding: "5px 12px", backgroundColor: "#dc3545", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }
};